import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useCan } from '../../engine/api/context';
import { useResourceList } from '../../engine/api/hooks';
import { Select } from '../../engine/design/primitives/Input';
import { RelationPicker } from '../../engine/fields/RelationPicker';
import type { FieldMeta } from '../../engine/meta/types';

// Home scope in the URL (`/?technician=<id>&client=<id>`), so a scoped Home is a shareable link:
// a manager (technicians:capacity) reads another technician's week and "mine" tiles; anyone can
// narrow the attention band and receivables to one client. The API enforces both.

export interface HomeScopeParams {
  technician_id?: string;
  client_id?: string;
}

export function useHomeScope() {
  const [sp, setSp] = useSearchParams();
  const scope = useMemo<HomeScopeParams>(() => {
    const out: HomeScopeParams = {};
    const t = sp.get('technician');
    const c = sp.get('client');
    if (t) out.technician_id = t;
    if (c) out.client_id = c;
    return out;
  }, [sp]);
  const set = useCallback(
    (name: 'technician' | 'client', value: string | null) => {
      setSp((prev) => {
        const next = new URLSearchParams(prev);
        if (value) next.set(name, value);
        else next.delete(name);
        return next;
      }, { replace: true });
    },
    [setSp],
  );
  return { scope, set };
}

const CLIENT_FIELD: FieldMeta = {
  name: 'client_id', label: 'Client', type: 'relation', relation: { resource: 'clients', displayField: 'name' },
  required: false, readOnly: false, nullable: true, filterable: false, sortable: false, searchable: false, inCreate: false, inUpdate: false, inList: false, inDetail: false,
};

export function ScopeBar({ scope, set, me, clientName }: { scope: HomeScopeParams; set: (n: 'technician' | 'client', v: string | null) => void; me: string | null; clientName: string | null }) {
  const manager = useCan('technicians:capacity');
  const techs = useResourceList<{ id: string; name: string; status?: string }>('technicians', { sort: ['name'] }, { limit: 200, enabled: manager, staleTime: 300_000 });
  return (
    <div className="hf-row hf-home-scope" role="group" aria-label="Home scope">
      {manager && (
        <Select
          size="sm"
          aria-label="Technician"
          value={scope.technician_id ?? me ?? ''}
          onChange={(e) => set('technician', e.target.value && e.target.value !== me ? e.target.value : null)}
          options={[...(me ? [] : [{ value: '', label: 'No technician' }]), ...techs.rows.filter((t) => t.status !== 'inactive').map((t) => ({ value: t.id, label: t.id === me ? `${t.name} (me)` : t.name }))]}
        />
      )}
      <span style={{ minWidth: '12rem' }}>
        <RelationPicker field={CLIENT_FIELD} row={clientName ? { client_name: clientName } : undefined} value={scope.client_id ?? null} onChange={(v) => set('client', v)} size="sm" aria-label="Client" />
      </span>
    </div>
  );
}
